import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../../core/platform/auth/AuthProvider";
import { uuidv7 } from "../../../core/platform/id";
import {
  UNITS,
  UNIT_LABELS,
  formatAmount,
  formatPesos,
  formatQuantity,
  parseQuantity,
  QuantityError,
  type Unit,
} from "../quantity";
import type { Ingredient, InventoryApi, Movement, Reconciliation } from "./api";
import { MovementForm, type MovementKind } from "./MovementForm";

/**
 * Ingredients: what is on the shelf, and the three ways it changes by hand
 * (T021, T026, T029, T032, T038).
 *
 * NO STOCK FIELD, ANYWHERE. An ingredient is created at zero and uncounted;
 * its opening figure is a count, and every change after that is a movement
 * that records who, when and why. That is what lets the ledger be believed.
 */

const PAGE_SIZE = 6;

const STATE_LABEL: Record<Ingredient["state"], string> = {
  ok: "OK",
  low: "Low",
  negative: "Below zero",
  uncounted: "Not counted",
};

const CAUSE_LABEL: Record<Movement["cause"], string> = {
  sale: "Sold",
  sale_reversal: "Void / refund",
  wastage: "Used / wasted",
  receiving: "Delivered",
  count_adjustment: "Counted",
};

function stockText(ingredient: Ingredient): string {
  // Never counted is not the same as none (FR-119).
  if (ingredient.state === "uncounted" && ingredient.currentStock === 0) return "—";
  return formatQuantity(ingredient.currentStock, ingredient.unit);
}

export interface IngredientsProps {
  api: InventoryApi;
  /** Passed to the delivery form, which may also record what was paid (T015). */
  token: string;
  onChanged(): void | Promise<void>;
}

export function Ingredients({ api, token, onChanged }: IngredientsProps) {
  const { can } = useAuth();
  const canWrite = can("inventory.write");
  const canMove = can("inventory.movement");

  const [ingredients, setIngredients] = useState<Ingredient[] | null>(null);
  const [page, setPage] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editing, setEditing] = useState<Ingredient | "new" | null>(null);
  const [moving, setMoving] = useState<MovementKind | null>(null);
  const [movements, setMovements] = useState<Movement[]>([]);
  const [books, setBooks] = useState<Reconciliation | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setIngredients((await api.ingredients()).ingredients);
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not load ingredients");
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const selected = ingredients?.find((ingredient) => ingredient.id === selectedId) ?? null;

  useEffect(() => {
    if (!selectedId) return;
    let cancelled = false;
    void api
      .movements(selectedId)
      .then((body) => !cancelled && setMovements(body.movements))
      .catch(() => !cancelled && setMovements([]));
    return () => {
      cancelled = true;
    };
  }, [api, selectedId, ingredients]);

  const afterChange = async (message: string) => {
    setNotice(message);
    setMoving(null);
    setEditing(null);
    await load();
    await onChanged();
  };

  if (editing) {
    return (
      <IngredientForm
        api={api}
        ingredient={editing === "new" ? null : editing}
        onDone={(message, id) => {
          if (id) setSelectedId(id);
          void afterChange(message);
        }}
        onCancel={() => setEditing(null)}
      />
    );
  }

  if (moving && selected) {
    return (
      <MovementForm
        api={api}
        token={token}
        ingredient={selected}
        kind={moving}
        onDone={(message) => void afterChange(message)}
        onCancel={() => setMoving(null)}
      />
    );
  }

  if (selected) {
    return (
      <section className="inv__panel">
        <div className="inv__detail-head">
          <button type="button" className="inv__back" onClick={() => setSelectedId(null)}>
            ‹ All ingredients
          </button>
          <span className={`inv__chip is-${selected.state}`}>{STATE_LABEL[selected.state]}</span>
        </div>

        <h3 className="inv__detail-name">{selected.name}</h3>
        <dl className="inv__facts">
          <div>
            <dt>On hand</dt>
            <dd>{stockText(selected)}</dd>
          </div>
          <div>
            <dt>Warn at</dt>
            <dd>
              {selected.lowStockThreshold === null
                ? "Never"
                : formatQuantity(selected.lowStockThreshold, selected.unit)}
            </dd>
          </div>
          <div>
            <dt>Last paid</dt>
            <dd>
              {selected.lastUnitCost === null
                ? "—"
                : `${formatPesos(selected.lastUnitCost)} per ${selected.unit === "piece" ? "piece" : selected.unit}`}
            </dd>
          </div>
          <div>
            <dt>Last counted</dt>
            <dd>{selected.countedAt ? new Date(selected.countedAt).toLocaleString() : "Never"}</dd>
          </div>
        </dl>

        {selected.state === "negative" && (
          <p className="inv__warning" role="status">
            Below zero means the books are wrong, not that it ran out — more was used than was ever
            recorded coming in. Count it to put it right.
          </p>
        )}

        <div className="inv__buttons">
          {canMove && (
            <>
              <button type="button" className="inv__action is-primary" onClick={() => setMoving("count")}>
                Count
              </button>
              <button type="button" className="inv__action" onClick={() => setMoving("receiving")}>
                Delivery
              </button>
              <button type="button" className="inv__action" onClick={() => setMoving("wastage")}>
                Used / wasted
              </button>
            </>
          )}
          {canWrite && (
            <button type="button" className="inv__action" onClick={() => setEditing(selected)}>
              Edit
            </button>
          )}
        </div>

        {notice && (
          <p className="inv__notice" role="status">
            {notice}
          </p>
        )}

        <h4 className="inv__subhead">Recent</h4>
        {movements.length === 0 ? (
          <p className="inv__empty">Nothing recorded yet.</p>
        ) : (
          <ul className="inv__list inv__list--compact">
            {movements.map((movement) => (
              <li key={movement.id} className="inv__row">
                <span className="inv__row-name">
                  {CAUSE_LABEL[movement.cause]}
                  {movement.reason ? ` — ${movement.reason}` : ""}
                </span>
                <span className={`inv__row-figure${movement.quantityDelta < 0 ? " is-out" : ""}`}>
                  {movement.quantityDelta > 0 ? "+" : ""}
                  {formatQuantity(movement.quantityDelta, movement.unit)}
                </span>
                <span className="inv__row-meta">
                  {movement.staffName ?? "—"} · {new Date(movement.createdAt).toLocaleString()}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    );
  }

  const list = ingredients ?? [];
  const pages = Math.max(Math.ceil(list.length / PAGE_SIZE), 1);
  const current = Math.min(page, pages - 1);
  const visible = list.slice(current * PAGE_SIZE, (current + 1) * PAGE_SIZE);

  return (
    <section className="inv__panel">
      {error && (
        <p className="inv__error" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <p className="inv__notice" role="status">
          {notice}
        </p>
      )}

      {ingredients === null ? (
        <p className="inv__empty">Loading…</p>
      ) : list.length === 0 ? (
        <p className="inv__empty">
          No ingredients yet. Add the things the café buys — milk, beans, cups — then count each one
          to open its books.
        </p>
      ) : (
        <ul className="inv__list">
          {visible.map((ingredient) => (
            <li key={ingredient.id}>
              <button type="button" className="inv__row inv__row--button" onClick={() => setSelectedId(ingredient.id)}>
                <span className="inv__row-name">{ingredient.name}</span>
                <span className="inv__row-figure">{stockText(ingredient)}</span>
                <span className={`inv__chip is-${ingredient.state}`}>{STATE_LABEL[ingredient.state]}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="inv__foot">
        {pages > 1 && (
          <div className="inv__pager">
            <button type="button" className="inv__action" disabled={current === 0} onClick={() => setPage(current - 1)}>
              ‹
            </button>
            <span aria-live="polite">
              {current + 1} of {pages}
            </span>
            <button
              type="button"
              className="inv__action"
              disabled={current >= pages - 1}
              onClick={() => setPage(current + 1)}
            >
              ›
            </button>
          </div>
        )}
        <div className="inv__buttons">
          {canWrite && (
            <button type="button" className="inv__action is-primary" onClick={() => setEditing("new")}>
              + Ingredient
            </button>
          )}
          <button
            type="button"
            className="inv__action"
            onClick={async () => {
              try {
                setBooks(await api.reconciliation());
              } catch (caught) {
                setError(caught instanceof Error ? caught.message : "Could not check the books");
              }
            }}
          >
            Check the books
          </button>
        </div>
      </div>

      {books && (
        <div className={`inv__books${books.balanced ? " is-balanced" : ""}`} role="status">
          {books.balanced ? (
            <p>Every ingredient's stock matches its history exactly.</p>
          ) : (
            <>
              <p>These don't match their recorded history. A count puts them right:</p>
              <ul>
                {books.ingredients
                  .filter((ingredient) => ingredient.difference !== 0)
                  .map((ingredient) => (
                    <li key={ingredient.id}>
                      {ingredient.name}: off by {formatQuantity(ingredient.difference, ingredient.unit)}
                    </li>
                  ))}
              </ul>
            </>
          )}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------

interface IngredientFormProps {
  api: InventoryApi;
  ingredient: Ingredient | null;
  onDone(message: string, id?: string): void;
  onCancel(): void;
}

function IngredientForm({ api, ingredient, onDone, onCancel }: IngredientFormProps) {
  const [name, setName] = useState(ingredient?.name ?? "");
  const [unit, setUnit] = useState<Unit>(ingredient?.unit ?? "l");
  const [threshold, setThreshold] = useState(
    ingredient?.lowStockThreshold === null || ingredient?.lowStockThreshold === undefined
      ? ""
      : formatAmount(ingredient.lowStockThreshold),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [id] = useState(() => ingredient?.id ?? uuidv7());

  const save = async () => {
    setError(null);
    let lowStockThreshold: number | null = null;
    try {
      lowStockThreshold = threshold.trim() === "" ? null : parseQuantity(threshold);
    } catch (caught) {
      setError(caught instanceof QuantityError ? caught.message : "That warning level is not a number.");
      return;
    }
    if (name.trim() === "") {
      setError("Give it a name.");
      return;
    }

    setBusy(true);
    try {
      if (ingredient) {
        await api.updateIngredient(ingredient.id, {
          name: name.trim(),
          lowStockThreshold,
          ...(unit !== ingredient.unit ? { unit } : {}),
        });
        onDone(`${name.trim()} saved.`, ingredient.id);
      } else {
        await api.createIngredient({ id, name: name.trim(), unit, lowStockThreshold });
        onDone(`${name.trim()} added. Count it to open its books.`, id);
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not save");
    } finally {
      setBusy(false);
    }
  };

  const retire = async () => {
    if (!ingredient) return;
    setBusy(true);
    setError(null);
    try {
      await api.retireIngredient(ingredient.id);
      onDone(`${ingredient.name} retired. Its history is kept.`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not retire it");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="inv__panel inv__form" aria-label={ingredient ? `Edit ${ingredient.name}` : "New ingredient"}>
      <h3 className="inv__detail-name">{ingredient ? ingredient.name : "New ingredient"}</h3>

      <label className="inv__field">
        <span>Name</span>
        <input value={name} onChange={(event) => setName(event.target.value)} maxLength={120} autoFocus />
      </label>

      <label className="inv__field">
        <span>Counted in</span>
        <select value={unit} onChange={(event) => setUnit(event.target.value as Unit)}>
          {UNITS.map((option) => (
            <option key={option} value={option}>
              {UNIT_LABELS[option]}
            </option>
          ))}
        </select>
      </label>

      <label className="inv__field">
        <span>Warn me when it gets down to ({unit === "piece" ? "pieces" : unit}) — leave empty for never</span>
        <input
          value={threshold}
          onChange={(event) => setThreshold(event.target.value)}
          inputMode="decimal"
          placeholder="e.g. 2"
        />
      </label>

      {error && (
        <p className="inv__error" role="alert">
          {error}
        </p>
      )}

      <div className="inv__buttons">
        <button type="button" className="inv__action" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        {ingredient && (
          <button type="button" className="inv__action is-danger" onClick={() => void retire()} disabled={busy}>
            Retire
          </button>
        )}
        <button type="button" className="inv__action is-primary" onClick={() => void save()} disabled={busy}>
          Save
        </button>
      </div>
    </section>
  );
}
